import type { PaginationMetadata } from "@/schema"
import type {
  BulkImportItem,
  BulkImportResponse,
  CommissionerApplyFiltersResponse,
  CommissionerApplyMappingsResponse,
  CommissionerBulkUpdateItem,
  CommissionerDataImportResponse,
  CommissionerFilterOptionsResponse,
  CommissionerFilterSelection,
  CommissionerHouseholdResponse,
  CommissionerReviewItem,
  CommissionerReviewItemsResponse,
  HouseholdCreateParams,
  ImportAs,
  RetryImportResponse,
} from "@/schema/commissioner-data-import"
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"

import { ApiContext, apiContextFetch } from "@/hooks/api/base"
import type { ReviewItemsParams } from "@/hooks/api/commissioner/data-imports/api"
import { useApiContext } from "@/hooks/api/use-api-context"
import { useBookId } from "@/hooks/use-book-id"

import type { DataImportApi } from "./context"

const LIVE_STATUSES = new Set(["importing", "extracting", "scraping", "pending", "awaiting_2fa"])
const POLL_INTERVAL_MS = 3000

function fetchDataImport(context: ApiContext, id: string) {
  return apiContextFetch<CommissionerDataImportResponse>(context, `data_imports/${id}`)
}

function fetchFilterOptions(context: ApiContext, id: string) {
  return apiContextFetch<CommissionerFilterOptionsResponse>(context, `data_imports/${id}/filter_options`)
}

function fetchReviewItems(context: ApiContext, id: string, params?: ReviewItemsParams) {
  const qp = new URLSearchParams()
  if (params?.import_status) qp.set("import_status", params.import_status)
  if (params?.type) qp.set("type", params.type)
  if (params?.query) qp.set("query", params.query)
  if (params?.sort_by) qp.set("sort_by", params.sort_by)
  if (params?.sort_direction) qp.set("sort_direction", params.sort_direction)
  if (params?.page) qp.set("page", params.page.toString())
  if (params?.per_page) qp.set("per_page", params.per_page.toString())

  const qs = qp.toString()
  return apiContextFetch<CommissionerReviewItemsResponse>(
    context,
    `data_imports/${id}/review_items${qs ? `?${qs}` : ""}`
  )
}

function applyFilters(
  context: ApiContext,
  id: string,
  params: { import_mode: "all" | "filtered"; import_filters?: CommissionerFilterSelection[] }
) {
  return apiContextFetch<CommissionerApplyFiltersResponse>(context, `data_imports/${id}/filters`, {
    method: "POST",
    body: JSON.stringify(params),
  })
}

function applyMappings(
  context: ApiContext,
  id: string,
  params: {
    client_filters?: CommissionerFilterSelection[]
    past_client_filters?: CommissionerFilterSelection[]
    prospect_filters?: CommissionerFilterSelection[]
    import_remaining_items_as?: "other_contact" | "prospect"
  }
) {
  return apiContextFetch<CommissionerApplyMappingsResponse>(context, `data_imports/${id}/mappings`, {
    method: "POST",
    body: JSON.stringify(params),
  })
}

function bulkUpdateReviewItems(context: ApiContext, id: string, updates: CommissionerBulkUpdateItem[]) {
  return apiContextFetch<void>(context, `data_imports/${id}/bulk_updates`, {
    method: "POST",
    body: JSON.stringify({ updates }),
  })
}

function startImport(context: ApiContext, id: string) {
  return apiContextFetch<{ data_import: { id: string; status: string; wizard_step: string } }>(
    context,
    `data_imports/${id}/start`,
    { method: "POST" }
  )
}

function updateDataImport(
  context: ApiContext,
  id: string,
  params: { wizard_step: "filter" | "data_mappings" | "review" }
) {
  return apiContextFetch<CommissionerDataImportResponse>(context, `data_imports/${id}`, {
    method: "PATCH",
    body: JSON.stringify({ data_import: params }),
  })
}

function detailKey(bookId: string | null, id: string | null) {
  return ["self-serve", "data-import", bookId, id] as const
}

function filterOptionsKey(bookId: string | null, id: string | null) {
  return ["self-serve", "data-import", bookId, id, "filter-options"] as const
}

function reviewItemsKey(bookId: string | null, id: string | null, params: ReviewItemsParams | undefined) {
  return ["self-serve", "data-import", bookId, id, "review-items", params] as const
}

function reviewItemsPrefixKey(bookId: string | null, id: string | null) {
  return ["self-serve", "data-import", bookId, id, "review-items"] as const
}

function useSelfServeBookContext(bookIdOverride?: string | null) {
  const urlBookId = useBookId()
  const bookId = bookIdOverride ?? urlBookId ?? null
  const getContext = useApiContext(bookId ?? undefined)

  return { bookId, getContext }
}

function useFetchDataImport(id: string | null, bookIdOverride?: string | null) {
  const { bookId, getContext } = useSelfServeBookContext(bookIdOverride)
  return useQuery({
    queryKey: detailKey(bookId, id),
    queryFn: async () => {
      const ctx = await getContext()
      if (!ctx) throw new Error("No context found")
      if (!id) throw new Error("No data import ID")
      return fetchDataImport(ctx, id)
    },
    enabled: !!id && !!bookId,
    staleTime: 0,
    refetchInterval: (query) =>
      LIVE_STATUSES.has(query.state.data?.data_import?.status ?? "") ? POLL_INTERVAL_MS : false,
  })
}

function useFetchFilterOptions(id: string | null, bookIdOverride?: string | null) {
  const { bookId, getContext } = useSelfServeBookContext(bookIdOverride)
  return useQuery({
    queryKey: filterOptionsKey(bookId, id),
    queryFn: async () => {
      const ctx = await getContext()
      if (!ctx) throw new Error("No context found")
      if (!id) throw new Error("No data import ID")
      return fetchFilterOptions(ctx, id)
    },
    enabled: !!id && !!bookId,
  })
}

function useFetchReviewItems(
  id: string | null,
  params?: ReviewItemsParams,
  { poll = false }: { poll?: boolean } = {},
  bookIdOverride?: string | null
) {
  const { bookId, getContext } = useSelfServeBookContext(bookIdOverride)
  return useInfiniteQuery<
    CommissionerReviewItemsResponse,
    Error,
    { review_items: CommissionerReviewItem[]; pagination: PaginationMetadata },
    ReturnType<typeof reviewItemsKey>,
    number | undefined
  >({
    queryKey: reviewItemsKey(bookId, id, params),
    queryFn: async ({ pageParam }) => {
      const ctx = await getContext()
      if (!ctx) throw new Error("No context found")
      if (!id) throw new Error("No data import ID")
      return fetchReviewItems(ctx, id, { ...params, page: pageParam })
    },
    enabled: !!id && !!bookId,
    staleTime: 0,
    refetchInterval: poll ? POLL_INTERVAL_MS : false,
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => lastPage.pagination.next_page_params?.page ?? undefined,
    select: (data) => ({
      review_items: data.pages.flatMap((p) => p.review_items),
      pagination: data.pages[data.pages.length - 1]?.pagination,
    }),
  })
}

function useApplyFilters(id: string, bookIdOverride?: string | null) {
  const { bookId, getContext } = useSelfServeBookContext(bookIdOverride)
  const qc = useQueryClient()

  return useMutation<
    CommissionerApplyFiltersResponse,
    Error,
    { import_mode: "all" | "filtered"; import_filters?: CommissionerFilterSelection[] }
  >({
    mutationFn: async (params) => {
      const ctx = await getContext()
      if (!ctx) throw new Error("No context found")
      return applyFilters(ctx, id, params)
    },
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: detailKey(bookId, id) }),
        qc.invalidateQueries({ queryKey: ["data-import-sources"] }),
      ]),
  })
}

function useApplyMappings(id: string, bookIdOverride?: string | null) {
  const { bookId, getContext } = useSelfServeBookContext(bookIdOverride)
  const qc = useQueryClient()

  return useMutation<
    CommissionerApplyMappingsResponse,
    Error,
    {
      client_filters?: CommissionerFilterSelection[]
      past_client_filters?: CommissionerFilterSelection[]
      prospect_filters?: CommissionerFilterSelection[]
      import_remaining_items_as?: "other_contact" | "prospect"
    }
  >({
    mutationFn: async (params) => {
      const ctx = await getContext()
      if (!ctx) throw new Error("No context found")
      return applyMappings(ctx, id, params)
    },
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: detailKey(bookId, id) }),
        qc.invalidateQueries({ queryKey: ["data-import-sources"] }),
      ]),
  })
}

function useBulkUpdateReviewItems(id: string, bookIdOverride?: string | null) {
  const { bookId, getContext } = useSelfServeBookContext(bookIdOverride)
  const qc = useQueryClient()

  return useMutation<void, Error, CommissionerBulkUpdateItem[]>({
    mutationFn: async (updates) => {
      const ctx = await getContext()
      if (!ctx) throw new Error("No context found")
      return bulkUpdateReviewItems(ctx, id, updates)
    },
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: reviewItemsPrefixKey(bookId, id) }),
        qc.invalidateQueries({ queryKey: detailKey(bookId, id) }),
      ]),
  })
}

function useStartImport(id: string, bookIdOverride?: string | null) {
  const { bookId, getContext } = useSelfServeBookContext(bookIdOverride)
  const qc = useQueryClient()

  return useMutation<{ data_import: { id: string; status: string; wizard_step: string } }, Error, void>({
    mutationFn: async () => {
      const ctx = await getContext()
      if (!ctx) throw new Error("No context found")
      return startImport(ctx, id)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: detailKey(bookId, id) })
      qc.invalidateQueries({ queryKey: ["data-import-sources"] })
    },
  })
}

function useUpdateDataImport(id: string, bookIdOverride?: string | null) {
  const { bookId, getContext } = useSelfServeBookContext(bookIdOverride)
  const qc = useQueryClient()

  return useMutation<CommissionerDataImportResponse, Error, { wizard_step: "filter" | "data_mappings" | "review" }>({
    mutationFn: async (params) => {
      const ctx = await getContext()
      if (!ctx) throw new Error("No context found")
      return updateDataImport(ctx, id, params)
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: detailKey(bookId, id) }),
  })
}

function fetchHousehold(context: ApiContext, dataImportId: string, householdId: string) {
  return apiContextFetch<CommissionerHouseholdResponse>(
    context,
    `data_imports/${dataImportId}/households/${householdId}`
  )
}

function updateHousehold(
  context: ApiContext,
  dataImportId: string,
  householdId: string,
  data: { import_as?: ImportAs; manual_create_params?: HouseholdCreateParams }
) {
  return apiContextFetch<CommissionerHouseholdResponse>(
    context,
    `data_imports/${dataImportId}/households/${householdId}`,
    { method: "PATCH", body: JSON.stringify(data) }
  )
}

function retryHouseholdImport(
  context: ApiContext,
  dataImportId: string,
  householdId: string,
  manualCreateParams?: HouseholdCreateParams
) {
  return apiContextFetch<RetryImportResponse>(
    context,
    `data_imports/${dataImportId}/households/${householdId}/retry_import`,
    { method: "POST", body: JSON.stringify(manualCreateParams ? { manual_create_params: manualCreateParams } : {}) }
  )
}

function retryBusinessImport(context: ApiContext, dataImportId: string, businessId: string) {
  return apiContextFetch<RetryImportResponse>(
    context,
    `data_imports/${dataImportId}/businesses/${businessId}/retry_import`,
    { method: "POST", body: JSON.stringify({}) }
  )
}

function bulkImport(context: ApiContext, dataImportId: string, items: BulkImportItem[]) {
  return apiContextFetch<BulkImportResponse>(context, `data_imports/${dataImportId}/bulk_import`, {
    method: "POST",
    body: JSON.stringify({ items }),
  })
}

function householdKey(bookId: string | null, id: string | null, householdId: string | null) {
  return ["self-serve", "data-import", bookId, id, "household", householdId] as const
}

function useFetchHousehold(id: string | null, householdId: string | null, bookIdOverride?: string | null) {
  const { bookId, getContext } = useSelfServeBookContext(bookIdOverride)
  return useQuery({
    queryKey: householdKey(bookId, id, householdId),
    queryFn: async () => {
      const ctx = await getContext()
      if (!ctx) throw new Error("No context found")
      if (!id || !householdId) throw new Error("Missing IDs")
      return fetchHousehold(ctx, id, householdId)
    },
    enabled: !!id && !!householdId && !!bookId,
  })
}

function useUpdateHousehold(id: string, bookIdOverride?: string | null) {
  const { bookId, getContext } = useSelfServeBookContext(bookIdOverride)
  const qc = useQueryClient()

  return useMutation({
    mutationFn: async ({
      householdId,
      data,
    }: {
      householdId: string
      data: { import_as?: ImportAs; manual_create_params?: HouseholdCreateParams }
    }) => {
      const ctx = await getContext()
      if (!ctx) throw new Error("No context found")
      return updateHousehold(ctx, id, householdId, data)
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: householdKey(bookId, id, variables.householdId) })
      qc.invalidateQueries({ queryKey: reviewItemsPrefixKey(bookId, id) })
      toast.success("Household updated")
    },
  })
}

function useRetryHouseholdImport(id: string, bookIdOverride?: string | null) {
  const { bookId, getContext } = useSelfServeBookContext(bookIdOverride)
  const qc = useQueryClient()

  return useMutation({
    mutationFn: async ({
      householdId,
      manualCreateParams,
    }: {
      householdId: string
      manualCreateParams?: HouseholdCreateParams
    }) => {
      const ctx = await getContext()
      if (!ctx) throw new Error("No context found")
      return retryHouseholdImport(ctx, id, householdId, manualCreateParams)
    },
    onSuccess: (data, variables) => {
      qc.invalidateQueries({ queryKey: householdKey(bookId, id, variables.householdId) })
      qc.invalidateQueries({ queryKey: reviewItemsPrefixKey(bookId, id) })
      qc.invalidateQueries({ queryKey: detailKey(bookId, id) })
      if (data.success) {
        toast.success("Import successful")
      } else {
        toast.error(data.error_message || "Import failed")
      }
    },
  })
}

function useRetryBusinessImport(id: string, bookIdOverride?: string | null) {
  const { bookId, getContext } = useSelfServeBookContext(bookIdOverride)
  const qc = useQueryClient()

  return useMutation({
    mutationFn: async ({ businessId }: { businessId: string }) => {
      const ctx = await getContext()
      if (!ctx) throw new Error("No context found")
      return retryBusinessImport(ctx, id, businessId)
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: reviewItemsPrefixKey(bookId, id) })
      qc.invalidateQueries({ queryKey: detailKey(bookId, id) })
      if (data.success) {
        toast.success("Import successful")
      } else {
        toast.error(data.error_message || "Import failed")
      }
    },
  })
}

function useBulkImport(id: string, bookIdOverride?: string | null) {
  const { bookId, getContext } = useSelfServeBookContext(bookIdOverride)
  const qc = useQueryClient()

  return useMutation({
    mutationFn: async ({ items }: { items: BulkImportItem[] }) => {
      const ctx = await getContext()
      if (!ctx) throw new Error("No context found")
      return bulkImport(ctx, id, items)
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: reviewItemsPrefixKey(bookId, id) })
      qc.invalidateQueries({ queryKey: detailKey(bookId, id) })
      const succeeded = data.results.filter((r) => r.success).length
      const failed = data.results.filter((r) => !r.success).length
      if (failed === 0) {
        toast.success(`Successfully imported ${succeeded} items`)
      } else {
        toast.warning(`Imported ${succeeded} items, ${failed} failed`)
      }
    },
  })
}

export const selfServeDataImportApi: DataImportApi = {
  useFetchDataImport,
  useFetchFilterOptions,
  useFetchReviewItems,
  useApplyFilters,
  useApplyMappings,
  useBulkUpdateReviewItems,
  useStartImport,
  useUpdateDataImport,
  useFetchHousehold,
  useUpdateHousehold,
  useRetryHouseholdImport,
  useRetryBusinessImport,
  useBulkImport,
}
